import { useEffect, useRef, useState } from 'react'
import Filmstrip from '../Filmstrip'
import Confetti from '../Confetti'
import {
  IconArrowRight, IconClapper, IconTrophy, IconStar, IconTicket, IconReplay,
} from '../icons'

function fmtTime(ms) {
  if (!ms) return '00:00'
  const s = Math.floor(ms / 1000)
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
}

/**
 * Co-Op results — shown to host + every player when status === 'coop-results'.
 *
 * Refund eligibility (computed client-side, applied locally):
 *   - Player whose turn never came (puzzle solved before reaching them)
 *   - Player who made 0 real moves across the whole round
 * Each player's device refunds itself when it lands on this screen.
 *
 * Host actions (only when forHost): onPlayAgain, onBackToLobby, onEndParty
 * Player action: onBackToLobby ("Back to Lobby" — reset local state + return to the player lobby)
 */
export default function PartyCoopDone({
  party,
  currentUserId,
  forHost,
  onPlayAgain,
  onBackToLobby,
  onEndParty,
  onRefundMyTicket, // (reason) => void — applied by App.jsx
}) {
  const players      = party?.players ?? {}
  const turnOrder    = party?.turnOrder ?? []
  const coopChain    = party?.coopChain ?? []
  const playerMoves  = party?.playerMoves ?? {}
  const startActor   = party?.startActor
  const goalActor    = party?.goalActor
  const cameoLastBy  = party?.cameoLastBy

  // Did the puzzle get solved? (last actor in chain matches goal)
  const lastActor = [...coopChain].reverse().find((c) => c.type === 'actor')
  const solved = lastActor && goalActor && lastActor.id === goalActor.id

  // Total hops = movie items in chain
  const totalHops = coopChain.filter((c) => c.type === 'movie').length

  // Total time
  const startMs = party?.playingStartedAt?.toMillis?.()
                ?? (party?.playingStartedAt?.seconds ? party.playingStartedAt.seconds * 1000 : null)
  const endMs   = party?.finishedAt?.toMillis?.()
                ?? (party?.finishedAt?.seconds ? party.finishedAt.seconds * 1000 : null)
  const totalMs = (startMs && endMs) ? endMs - startMs : 0

  // ── Refund logic (each device refunds itself) ─────────────────────────────
  // Blanket rule: zero moves = refund. The refund toast string is plain text
  // (no emoji); the toast surface adds its own visual chrome.
  const refundedRef = useRef(false)

  // Fire confetti when the results screen mounts after a solve.
  const [celebrating, setCelebrating] = useState(false)
  useEffect(() => {
    if (!solved) return
    setCelebrating(true)
    const id = setTimeout(() => setCelebrating(false), 4500)
    return () => clearTimeout(id)
  }, [solved])
  useEffect(() => {
    if (refundedRef.current) return
    if (forHost) return                    // host wasn't charged a ticket
    if (!currentUserId) return
    if (!onRefundMyTicket) return
    if (party?.status !== 'coop-results') return
    const myMoves = playerMoves[currentUserId] || 0
    if (myMoves > 0) return
    refundedRef.current = true
    onRefundMyTicket("The stars aligned a little too fast this time. Your ticket's been refunded — the players ahead of you were just too good!")
  }, [forHost, currentUserId, onRefundMyTicket, playerMoves, party?.status])

  const Matchup = () => (
    <p className="coop-done__matchup">
      <strong>{startActor?.name}</strong>
      <span className="coop-done__matchup-arrow" aria-hidden="true">
        <IconArrowRight size={16} />
      </span>
      <strong>{goalActor?.name}</strong>
    </p>
  )

  // ── HOST view ─────────────────────────────────────────────────────────────
  if (forHost) {
    return (
      <div className="coop-done coop-done--host">
        {celebrating && <Confetti />}

        <div className="coop-done__spotlight">
          <div className="coop-done__spotlight-icon" aria-hidden="true">
            <IconClapper size={32} />
          </div>
          <h2 className="coop-done__title">
            {solved ? 'What a performance!' : 'Time!'}
          </h2>
        </div>

        <Matchup />

        <div className="coop-done__score">
          <div className="coop-done__score-stat">
            <span className="coop-done__score-big">{totalHops}</span>
            <span className="coop-done__score-label">hops</span>
          </div>
          <div className="coop-done__score-stat">
            <span className="coop-done__score-big">{fmtTime(totalMs)}</span>
            <span className="coop-done__score-label">total time</span>
          </div>
        </div>

        {cameoLastBy && (
          <p className="coop-done__cameo-note">
            <IconStar size={14} aria-hidden="true" />
            <span>{cameoLastBy} called in a Cameo!</span>
          </p>
        )}

        {coopChain.length > 0 && (
          <div className="coop-done__chain coop-done__chain--filmstrip">
            <Filmstrip chain={coopChain} onItemClick={() => {}} hidePlaceholder />
          </div>
        )}

        <div className="coop-done__contributions">
          <p className="coop-done__contributions-label">CAST CONTRIBUTIONS</p>
          <div className="coop-done__contributions-grid">
            {turnOrder.map((uid) => {
              const p = players[uid]
              const moves = playerMoves[uid] || 0
              const refunded = moves === 0
              const initial = (p?.username || 'G').charAt(0).toUpperCase()
              return (
                <div key={uid} className="coop-done__contrib-chip">
                  <div className="coop-done__contrib-avatar">
                    {p?.avatar
                      ? <img src={p.avatar} alt="" />
                      : <span className="party-avatar__initial">{initial}</span>}
                  </div>
                  <span className="coop-done__contrib-name">{p?.username || 'Guest'}</span>
                  <span className="coop-done__contrib-moves">
                    {moves} move{moves !== 1 ? 's' : ''}
                  </span>
                  {refunded && (
                    <span className="party-refund-badge" title="Ticket refunded">
                      <IconTicket size={10} aria-hidden="true" />
                      <span>+1</span>
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        <div className="coop-done__actions">
          {onPlayAgain && (
            <button className="pb-action-btn pb-action-btn--primary" onClick={onPlayAgain}>
              <IconReplay size={18} aria-hidden="true" />
              <span>Play Again</span>
            </button>
          )}
          {onBackToLobby && (
            <button className="pb-action-btn pb-action-btn--outline" onClick={onBackToLobby}>
              Back to Lobby
            </button>
          )}
          {onEndParty && (
            <button className="pb-action-btn pb-action-btn--muted" onClick={onEndParty}>
              End Party
            </button>
          )}
        </div>
      </div>
    )
  }

  // ── PLAYER view ───────────────────────────────────────────────────────────
  const myMoves = playerMoves[currentUserId] || 0
  return (
    <div className="coop-done">
      {celebrating && <Confetti />}
      <h2
        className="coop-done__title"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}
      >
        {solved ? (
          <>
            <IconTrophy size={24} aria-hidden="true" />
            <span>You did it!</span>
          </>
        ) : 'Cut!'}
      </h2>
      <Matchup />

      <div className="coop-done__score">
        <div className="coop-done__score-stat">
          <span className="coop-done__score-big">{totalHops}</span>
          <span className="coop-done__score-label">hops</span>
        </div>
        <div className="coop-done__score-stat">
          <span className="coop-done__score-big">{fmtTime(totalMs)}</span>
          <span className="coop-done__score-label">time</span>
        </div>
      </div>

      <p className="coop-done__my-contrib">
        You contributed <strong>{myMoves}</strong> move{myMoves !== 1 ? 's' : ''}
        {myMoves === 0 && (
          <span className="party-refund-badge" title="Ticket refunded" style={{ marginLeft: 8 }}>
            <IconTicket size={10} aria-hidden="true" />
            <span>+1</span>
          </span>
        )}
      </p>

      {coopChain.length > 0 && (
        <div className="coop-done__chain">
          <Filmstrip chain={coopChain} onItemClick={() => {}} hidePlaceholder />
        </div>
      )}

      <p className="coop-done__waiting">Waiting for host...</p>

      <button className="pb-action-btn pb-action-btn--outline" onClick={onBackToLobby} style={{ marginTop: 16 }}>
        Back to Lobby
      </button>
    </div>
  )
}
